import React from "react";

// Define the data structure types
// ⚠️ Every name/contact field is nullable in practice — the kernel and blaze both return null
// for an unset name, and DataSharingTypes.Profile already types them `string | null`. They were
// declared as bare `string` here, which is what let `getDisplayName` be written as if a name
// always existed.
export interface Profile {
  id: number;
  username: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  avatar: string | null;
}

interface NotificationItem {
  /**
   * ⚠️ `string` is allowed on purpose. Blaze relationship ids are bare numbers; an ORG
   * invitation is keyed `org_invitation:<org_id>` so the dashboard's
   * `items.filter(i => i.id !== item.id)` can never drop the follow request that happens to
   * share that number.
   */
  id: number | string;
  status: "pending" | "accepted" | "declined";
  /**
   * ⛔ OPTIONAL. An ORG invitation has no honest age to show: the kernel's `invited_at` does NOT
   * move when an org re-invites, so printing "N days ago" from it would state something false.
   * The item omits the field, and the age line is omitted with it.
   */
  created_at?: string | null;
  type: "incoming_following_request" | "outgoing_following_request" | string;
  message: string;
  /**
   * ⛔ OPTIONAL. An action item is not always about a person: an ORG invitation carries no
   * profile at all and is rendered from `message` alone. Do not tighten this back to required.
   */
  profile?: Profile | null;
}

export interface ActionButton {
  text: string;
  action: (item: NotificationItem) => void;
  isPrimary?: boolean;
  variant?: "solid" | "outline" | "ghost";
}

interface ActionCardButtonsProps {
  item: NotificationItem;
  buttons: ActionButton[];
  className?: string;
}

export const ActionCardButtons: React.FC<ActionCardButtonsProps> = ({
  item,
  buttons,
  className = "",
}) => {
  const getButtonStyles = (button: ActionButton) => {
    const baseStyles =
      "flex-1 font-medium text-lg py-2 px-2 rounded-full transition-colors duration-200";

    if (button.isPrimary || button.variant === "solid") {
      return `${baseStyles} bg-cyan-500 hover:bg-cyan-600 text-white`;
    } else if (button.variant === "outline") {
      return `${baseStyles} bg-transparent border-2 border-cyan-500 text-cyan-500 hover:bg-cyan-50`;
    } else if (button.variant === "ghost") {
      return `${baseStyles} bg-transparent text-gray-600 hover:bg-gray-100`;
    } else {
      // Default outline style
      return `${baseStyles} bg-transparent border-2 border-cyan-500 text-cyan-500 hover:bg-cyan-50`;
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /**
   * The person's best display label — ALWAYS a string, never undefined.
   *
   * ⛔ This is the app-blanking path. It used to read `profile.last_name` off a parameter typed
   * as always-present, and to return `undefined` when a profile carried no name, phone or email;
   * `getTitle` then called `.trim()` on that result. Either threw a TypeError, and because the
   * app's only ErrorBoundary renders `null` (TD-165), a throw in one card blanks the WHOLE app —
   * web and native alike. Keep this total: return "" rather than a nullish value.
   */
  const getDisplayName = (profile?: Profile | null): string => {
    if (!profile) return "";
    const fullName = [profile.first_name, profile.last_name]
      .map((part) => part?.trim() ?? "")
      .filter(Boolean)
      .join(" ");
    // Joining the present parts (rather than interpolating both) also stops a missing first name
    // from rendering the literal "null Smith".
    return fullName || profile.phone?.trim() || profile.email?.trim() || "";
  };

  const getTitle = (): string => {
    // ⛔ Do NOT hoist getDisplayName above the switch. `default` is the branch that renders a
    // profile-less item (an ORG invitation) from its own `message`; reading the profile first
    // threw before that branch could ever be reached.
    switch (item.type) {
      case "incoming_following_request": {
        const displayName = getDisplayName(item.profile);
        return displayName
          ? `${displayName.slice(0, 30)} has invited you to view their data.`
          : item.message;
      }
      case "incoming_follower_request": {
        const displayName = getDisplayName(item.profile);
        return displayName
          ? `${displayName.slice(0, 30)} has requested to view your data.`
          : item.message;
      }
      default:
        return item.message;
    }
  };

  /**
   * "3 days ago" — or null when the item carries no honest timestamp.
   *
   * ⛔ Returning null rather than a string is the whole point. `new Date(undefined)` is an
   * Invalid Date, so the old inline version rendered the literal "NaN days ago" for any item
   * without a `created_at` — and an ORG invitation deliberately has none, because `invited_at`
   * survives a re-invite and would misstate the age of the ask.
   */
  const relativeAge = (dateString?: string | null): string | null => {
    if (!dateString) return null;
    // Parse created_at as local time if no timezone, otherwise as UTC.
    const created = new Date(dateString);
    if (isNaN(created.getTime())) return null;
    const diff = Math.floor((Date.now() - created.getTime()) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    return `${Math.floor(diff / 86400)} days ago`;
  };

  const age = relativeAge(item.created_at);

  return (
    <div
      className={` mx-auto bg-white rounded-3xl shadow-lg
         ${className}`}
      style={{ width: "100%" }}
    >
      {/* Title */}
      <h2 className="font-bold text-center text-gray-900">{getTitle()}</h2>
      {age !== null && (
        <div className="flex justify-center my-2">
          <small className="text-gray-500">{age}</small>
        </div>
      )}

      {/* Buttons Container - Row Layout */}
      <div className="flex flex-row">
        {buttons.map((button, index) => (
          <button
            key={index}
            onClick={() => button.action(item)}
            className={getButtonStyles(button)}
          >
            {button.text}
          </button>
        ))}
      </div>
    </div>
  );
};
